import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ArrowDown,
  ArrowRight,
  Globe,
  Palette,
  Terminal,
  Lightbulb,
  Megaphone,
  Search,
  BarChart3,
  Shield,
  Bot,
  Calendar,
  Database,
  Store,
  WholeWord as WordPress,
  Settings,
  Sparkles,
  CheckCircle2,
  ChevronDown
} from 'lucide-react';
import { Link } from 'react-router-dom';
import LatestNews from '../components/LatestNews';
import SEO from '../components/SEO';
import i18n from '../i18n';

const Home = () => {
  const { t } = useTranslation();
  // Debug logs
  console.log('LANG:', i18n.language);
  console.log('RESOURCES:', i18n.options.resources);
  console.log('home:', i18n.t('home', { returnObjects: true }));
  console.log('services:', i18n.t('services', { returnObjects: true }));
  console.log('common:', i18n.t('common', { returnObjects: true }));

  const services = [
    { 
      icon: Globe, 
      title: t('services.webDevelopment.title'),
      description: t('home.services.webDevelopment.description'),
      path: '/thjonusta/vefsidugerd',
      features: [
        t('home.services.webDevelopment.features.responsive'),
        t('home.services.webDevelopment.features.seo'),
        t('home.services.webDevelopment.features.fast')
      ]
    },
    { 
      icon: Palette, 
      title: t('services.webDesign.title'),
      description: t('home.services.webDesign.description'),
      path: '/thjonusta/vefhonnun',
      features: [
        t('home.services.webDesign.features.ux'),
        t('home.services.webDesign.features.branding'),
        t('home.services.webDesign.features.prototype')
      ]
    },
    { 
      icon: Terminal, 
      title: t('services.webProgramming.title'),
      description: t('home.services.webProgramming.description'),
      path: '/thjonusta/vefthroun',
      features: [
        t('home.services.webProgramming.features.custom'),
        t('home.services.webProgramming.features.api'),
        t('home.services.webProgramming.features.programming')
      ]
    },
    { 
      icon: Search, 
      title: t('services.seo.title'),
      description: t('home.services.seo.description'),
      path: '/thjonusta/leitarvelabestun',
      features: [
        t('home.services.seo.features.keywords'),
        t('home.services.seo.features.technical'),
        t('home.services.seo.features.content')
      ]
    },
    { 
      icon: Megaphone, 
      title: t('services.marketing.title'),
      description: t('home.services.marketing.description'),
      path: '/thjonusta/markadssetning',
      features: [
        t('home.services.marketing.features.social'),
        t('home.services.marketing.features.ads'),
        t('home.services.marketing.features.content')
      ]
    },
    { 
      icon: BarChart3, 
      title: t('services.analytics.title'),
      description: t('home.services.analytics.description'),
      path: '/thjonusta/greiningar',
      features: [
        t('home.services.analytics.features.google'),
        t('home.services.analytics.features.heatmap'),
        t('home.services.analytics.features.testing')
      ]
    }
  ];

  const solutions = [
    { 
      icon: Bot, 
      title: t('solutions.ai.title'),
      description: t('solutions.ai.description'),
      path: '/lausnir/gervigreind',
      features: [
        t('solutions.ai.features.chatbot'),
        t('solutions.ai.features.automation'),
        t('solutions.ai.features.analysis')
      ]
    },
    { 
      icon: Calendar, 
      title: t('solutions.booking.title'),
      description: t('solutions.booking.description'),
      path: '/lausnir/bokunarkerfi',
      features: [
        t('solutions.booking.features.online'),
        t('solutions.booking.features.payment'),
        t('solutions.booking.features.reminders')
      ]
    },
    { 
      icon: Database, 
      title: t('solutions.subscription.title'),
      description: t('solutions.subscription.description'),
      path: '/lausnir/askriftarkerfi',
      features: [
        t('solutions.subscription.features.management'),
        t('solutions.subscription.features.payment'),
        t('solutions.subscription.features.users')
      ]
    },
    { 
      icon: Store, 
      title: t('solutions.ecommerce.title'),
      description: t('solutions.ecommerce.description'),
      path: '/lausnir/netverslun',
      features: [
        t('solutions.ecommerce.features.products'),
        t('solutions.ecommerce.features.payment'),
        t('solutions.ecommerce.features.shipping')
      ]
    },
    { 
      icon: WordPress, 
      title: t('solutions.wordpressExpress.title'),
      description: t('solutions.wordpressExpress.description'),
      path: '/lausnir/wordpress-express',
      features: [
        t('solutions.wordpressExpress.features.price'),
        t('solutions.wordpressExpress.features.pages'),
        t('solutions.wordpressExpress.features.theme')
      ]
    },
    { 
      icon: Settings, 
      title: t('solutions.api.title'),
      description: t('solutions.api.description'),
      path: '/lausnir/api-tengingar',
      features: [
        t('solutions.api.features.custom'),
        t('solutions.api.features.integration'),
        t('solutions.api.features.security')
      ]
    }
  ];

  const customerLogos = [
    {
      name: 'Nathan og Olsen',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749669167/nathan_flfqem.svg'
    },
    {
      name: 'Tjarnarskóli',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460377/tjarnarskoli-logo_lwtekp.png'
    },
    {
      name: 'Rauði krossinn',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460378/logo-raudikross_ee5ean.png'
    },
    {
      name: 'Ásbjörn',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749669504/asbjorn_vdej0i.svg'
    },
    {
      name: 'Landsbókasafn',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749668413/logo_hmsljj.svg'
    },
    {
      name: 'Hjallastefnan',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749668835/hjallastefnan_j9ef51.svg'
    },
    {
      name: 'Gunnars',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749668315/cropped-gunnars_ehf_qiw1sz.png'
    },
    {
      name: 'Dufland',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749669111/idQaBBLbIk_logos_mowtco.png'
    },
    {
      name: 'Gorilla vöruhús',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1742386436/gorilla-logo_kldubf.png'
    },
    {
      name: 'Hagvangur',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749671255/hagvangur_tn64et.png'
    },
    {
      name: 'CarPark',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749671315/1c35f592-b8e9-4d25-848c-5389bd649aae.png'
    },
    {
      name: 'Tryggingavernd',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/v1749671386/a4c7ac75-f445-454a-96ad-a200c236ae31.png'
    }
  ];

  const stats = [
    { number: '500+', label: t('home.stats.clients') },
    { number: '600+', label: t('home.stats.projects') },
    { number: '99,9%', label: t('home.stats.uptime') },
    { number: '24/7', label: t('home.stats.support') }
  ];

  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 50
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const handleScrollDown = () => {
    const servicesSection = document.getElementById('services-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO 
        title={t('site.homeTitle')}
        description={t('site.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-[80vh] relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        {/* Animated background elements with more motion and visibility */}
        <motion.div
          className="absolute inset-0 z-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="absolute w-[600px] h-[600px] bg-[#EA295A]/20 rounded-full blur-3xl -top-40 -right-40"
            animate={{ y: [0, 60, 0], x: [0, -40, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute w-[400px] h-[400px] bg-blue-500/20 rounded-full blur-2xl top-32 -left-32"
            animate={{ y: [0, -40, 0], x: [0, 40, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
          <motion.div
            className="absolute w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-2xl bottom-0 -right-32"
            animate={{ y: [0, 50, 0], x: [0, -30, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
          {/* Extra animated shape for more interest */}
          <motion.div
            className="absolute w-[250px] h-[250px] bg-[#EA295A]/30 rounded-full blur-2xl top-1/2 left-1/4"
            animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
          />
        </motion.div>

        {/* Floating code elements with even more variety and fun */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none select-none">
          {/* HTML snippet */}
          <motion.div
            className="absolute top-20 left-10 text-white/10 font-mono text-xs md:text-sm"
            animate={{ y: [0, 20, 0], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            {`<div class="hero">`}
          </motion.div>
          {/* JS snippet */}
          <motion.div
            className="absolute top-40 right-20 text-blue-200/20 font-mono text-xs md:text-sm"
            animate={{ y: [0, -20, 0], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            {`const design = new Design();`}
          </motion.div>
          {/* CSS snippet */}
          <motion.div
            className="absolute bottom-40 left-1/4 text-pink-200/20 font-mono text-xs md:text-sm"
            animate={{ y: [0, 15, 0], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          >
            {`@media (max-width: 768px) {`}
          </motion.div>
          {/* React snippet */}
          <motion.div
            className="absolute top-10 right-1/4 text-green-200/20 font-mono text-xs md:text-sm rotate-2"
            animate={{ y: [0, 25, 0], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            {`function Hero() { return <Section />; }`}
          </motion.div>
          {/* JSON snippet */}
          <motion.div
            className="absolute bottom-16 right-10 text-yellow-100/20 font-mono text-xs md:text-sm -rotate-2"
            animate={{ y: [0, -18, 0], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
          >
            {`{"agency": "vefsida.is", "wow": true}`}
          </motion.div>
          {/* Extra HTML tag */}
          <motion.div
            className="absolute top-1/2 left-1/3 text-white/10 font-mono text-xs md:text-sm -rotate-3"
            animate={{ y: [0, 12, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          >
            {`<section id="cta">`}
          </motion.div>
          {/* Extra CSS rule */}
          <motion.div
            className="absolute bottom-10 left-10 text-blue-100/20 font-mono text-xs md:text-sm rotate-1"
            animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
          >
            {`.glass { backdrop-filter: blur(16px); }`}
          </motion.div>
          {/* Fun agency-themed snippet */}
          <motion.div
            className="absolute top-1/4 left-1/2 text-[#EA295A]/30 font-mono text-base md:text-lg rotate-6"
            animate={{ y: [0, 18, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          >
            {`// vefsida.is = wow`}
          </motion.div>
          {/* Fun JSON wow snippet, larger */}
          <motion.div
            className="absolute bottom-1/3 right-1/3 text-[#EA295A]/20 font-mono text-lg md:text-2xl rotate-[-8deg]"
            animate={{ y: [0, 30, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          >
            {`{"agency": "vefsida.is", "wow": true, "digital": "agency"}`}
          </motion.div>
          {/* Playful HTML closing tag */}
          <motion.div
            className="absolute bottom-1/4 left-1/5 text-green-200/20 font-mono text-lg md:text-xl rotate-12"
            animate={{ y: [0, 10, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
          >
            {`</wow>`}
          </motion.div>
          {/* Playful CSS variable */}
          <motion.div
            className="absolute top-1/3 right-1/5 text-blue-300/20 font-mono text-xs md:text-base -rotate-12"
            animate={{ y: [0, 14, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 8.5, repeat: Infinity, ease: "easeInOut" }}
          >
            {`--agency-wow: true;`}
          </motion.div>
        </div>

        <div className="relative flex items-center justify-center min-h-[80vh] z-20 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div 
              className="flex flex-col items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.12,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, scale: 0.8, y: -20 },
                  visible: { 
                    opacity: 1, 
                    scale: 1,
                    y: 0,
                    transition: {
                      type: "spring",
                      stiffness: 200,
                      damping: 10
                    }
                  }
                }}
                className="inline-flex items-center px-6 py-3 bg-white/10 rounded-full mb-8 backdrop-blur-sm border border-white/20"
              >
                <motion.div
                  animate={{ 
                    rotate: [0, 15, -15, 0],
                    scale: [1, 1.2, 1]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3
                  }}
                >
                  <Sparkles className="w-5 h-5 text-[#EA295A] mr-2" />
                </motion.div>
                <span className="text-white text-sm font-medium tracking-wide">{t('home.hero.badge')}</span>
              </motion.div>

              <motion.h1 
                className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight"
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      type: "spring",
                      stiffness: 100,
                      damping: 10
                    }
                  }
                }}
              >
                <span className="inline-block">
                  {t('home.hero.titlePrefix')}{' '}
                </span>
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-[#EA295A] to-[#FF6B6B] bg-clip-text text-transparent">
                    {t('home.hero.titleHighlight')}
                  </span>
                  <motion.span 
                    className="absolute -bottom-1 left-0 right-0 h-[6px] bg-gradient-to-r from-[#EA295A] to-[#FF6B6B] rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ 
                      duration: 0.8, 
                      delay: 1.2,
                      ease: "easeOut"
                    }}
                  />
                </span>
              </motion.h1>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.8,
                      delay: 0.6
                    }
                  }
                }}
                className="text-xl text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto"
              >
                {t('home.hero.description')}
              </motion.p>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.8,
                      delay: 0.8
                    }
                  }
                }}
                className="flex flex-col sm:flex-row gap-6 justify-center"
              >
                <Link to="/thjonusta">
                  <motion.button
                    whileHover={{ scale: 1.08, backgroundColor: "rgba(234,41,90,0.9)" }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-[#EA295A] text-white px-10 py-5 rounded-xl text-lg font-semibold transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                  >
                    {t('home.hero.exploreServices')}
                    <ArrowRight className="ml-2" />
                  </motion.button>
                </Link>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.08, backgroundColor: "rgba(255,255,255,0.2)" }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white/10 backdrop-blur-sm text-white px-10 py-5 rounded-xl text-lg font-semibold transition-all border border-white/20 hover:bg-white/20 flex items-center"
                  >
                    {t('common.getQuote')}
                    <ArrowRight className="ml-2" />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
        
        {/* Scroll down button - moved further down and more floaty */}
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 bottom-2 md:bottom-8 z-20 cursor-pointer"
          onClick={handleScrollDown}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          transition={{ delay: 1 }}
        >
          <div className="flex flex-col items-center">
            <motion.div
              className="flex flex-col items-center"
              animate={{ y: [0, 18, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.5 }}
            >
              <motion.div 
                className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg flex items-center justify-center"
                whileHover={{ scale: 1.13, backgroundColor: "rgba(255, 255, 255, 0.3)" }}
              >
                <ArrowDown className="w-5 h-5 text-white" />
                <span className="sr-only">{t('home.hero.scrollDown')}</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.div 
        className="py-24 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        id="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="text-center"
                variants={itemVariants}
              >
                <motion.div 
                  className="text-4xl font-bold text-[#EA295A] mb-2"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {stat.number}
                </motion.div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Minimalist animated logo wall */}
      <motion.div 
        className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-100"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1 mb-4 bg-[#EA295A]/10 text-[#EA295A] rounded-full font-semibold text-sm tracking-wide">
              {t('home.clients.title')}
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('home.clients.description')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
            {customerLogos.map((logo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.08 * index, duration: 0.5, type: 'spring', stiffness: 120 }}
                className="flex items-center justify-center"
              >
                <img
                  src={logo.logo}
                  alt={logo.name}
                  className="max-h-20 max-w-[180px] w-full object-contain mx-auto my-auto drop-shadow-md transition-all duration-300 hover:scale-110 hover:drop-shadow-[0_4px_24px_rgba(234,41,90,0.25)]"
                  style={{ opacity: 1 }}
                />
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/verkefni" className="inline-flex items-center text-[#EA295A] font-medium hover:text-[#d01d4c]">
              {t('common.seeAllProjects')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div 
        className="py-24 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('common.services')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.services.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index} 
                className="bg-gray-50 rounded-xl p-8"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="bg-[#EA295A]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.path}
                  className="mt-6 inline-flex items-center text-[#EA295A] hover:text-[#d01d4c] font-medium"
                >
                  {t('common.readMore')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="py-24 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('common.solutions')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.solutions.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div 
                key={index} 
                className="bg-white rounded-xl p-8 shadow-sm"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="bg-[#EA295A]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  <solution.icon className="w-7 h-7 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-3">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={solution.path}
                  className="mt-6 inline-flex items-center text-[#EA295A] hover:text-[#d01d4c] font-medium"
                >
                  {t('common.readMore')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <LatestNews />

      {/* Modern Glassmorphism CTA Section */}
      <motion.div 
        className="relative py-32 flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-gray-100 overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        {/* Subtle blurred shape for depth */}
        <div className="absolute -top-32 -left-32 w-[400px] h-[400px] bg-[#EA295A]/10 rounded-full blur-3xl z-0" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-blue-400/10 rounded-full blur-2xl z-0" />
        <div className="relative z-10 w-full max-w-2xl mx-auto px-6">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl px-10 py-16 flex flex-col items-center text-center border border-white/40">
            <Sparkles className="w-8 h-8 text-[#EA295A] mb-4 animate-pulse" />
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 drop-shadow-sm">
              {t('home.cta.title')}
            </h2>
            <p className="text-xl text-gray-700 mb-10 max-w-xl">
              {t('home.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-[#EA295A] text-white px-10 py-5 rounded-xl text-lg font-semibold transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  {t('common.getQuote')}
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
              <Link to="/verkefni">
                <motion.button
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-white/80 backdrop-blur-sm text-[#EA295A] px-10 py-5 rounded-xl text-lg font-semibold transition-all border border-[#EA295A]/20 hover:bg-white/90 flex items-center"
                >
                  {t('common.seeAllProjects')}
                  <Sparkles className="ml-2 w-5 h-5 text-[#EA295A]" />
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Home;